import { LucideIcon, Carousel, type CarouselApi, useBreakpointValue } from '@skin-seoul/design-system';

import { AnimatePresence, motion } from 'motion/react';
import { StarIcon } from '../assets/icons/StarIcon';
import { useEffect, useMemo, useState, useCallback } from 'react';
import type { ReviewImage, Review } from '..';
import { formatRelativeTime } from '@skin-seoul/service/src/utils/review/dateUtils';
import { ReviewImageDetailModal } from './ReviewImageDetailModal';
import avatar from '../assets/images/avatar.png';
interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="fixed top-0 left-0 right-0 bottom-0 bg-black/70 z-[999998] flex items-center justify-center"
    {...props}
  >
    {children}
  </motion.div>
);

const ModalContainer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="relative w-[90vw] max-w-[1000px] max-h-[90dvh] bg-white rounded-[20px] z-[999999] overflow-hidden flex flex-col"
    {...props}
  >
    {children}
  </motion.div>
);

const MobileDrawer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="fixed left-0 right-0 bottom-0 max-h-[90dvh] bg-white rounded-t-[20px] z-[999999] overflow-hidden flex flex-col"
    style={{ touchAction: 'none' }}
    {...props}
  >
    {children}
  </motion.div>
);

const EmptyState = ({ children }: { children?: React.ReactNode }) => (
  <div className="flex flex-col items-center justify-center h-[200px] text-[#666] text-center">{children}</div>
);

const EmptyStateText = ({ children }: { children?: React.ReactNode }) => (
  <p className="text-[16px] m-0">
    {children}
  </p>
);

interface ImageGalleryContentProps {
  images: ReviewImage[];
  onImageClick: (image: ReviewImage) => void;
}

const ImageGalleryContent = ({ images, onImageClick }: ImageGalleryContentProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  if (images.length === 0) {
    return (
      <EmptyState>
        <EmptyStateText>No customer images yet</EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <div className={`grid grid-cols-2 ${isMobile ? 'gap-3 pt-0' : 'gap-[12px] pt-[32px]'} pb-[24px]`}>
      {images.map((image, index) => (
        <img
          key={image.id || index}
          src={image.url}
          alt={image.alt || `Customer image ${index + 1}`}
          loading={index < 6 ? 'eager' : 'lazy'}
          className="rounded-xl w-full object-cover cursor-pointer"
          style={{ aspectRatio: isMobile ? '158 / 235' : '470 / 235' }}
          onClick={() => onImageClick(image)}
          onError={(_e: React.SyntheticEvent<HTMLImageElement, Event>) => {
            console.error(`Failed to load image: ${image.url}`);
          }}
        />
      ))}
    </div>
  );
};

interface ReviewDetailContentProps {
  review: Review;
  selectedImageId?: string;
  onImageClick: (image: ReviewImage) => void;
}

const ReviewDetailContent = ({ review, selectedImageId, onImageClick }: ReviewDetailContentProps) => {
  const reviewImages = useMemo(() => review.images || [], [review]);
  const hasReviewImages = Boolean(review.images && review.images.length > 0);
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  const scrollToImage = useCallback(
    (imageId: string) => {
      if (isMobile) {
        const element = document.getElementById(`review-image-${imageId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      } else if (carouselApi) {
        const imageIndex = reviewImages.findIndex((image) => image.id === imageId);
        if (imageIndex !== -1) {
          carouselApi.scrollTo(imageIndex);
        }
      }
    },
    [isMobile, carouselApi, reviewImages],
  );

  useEffect(() => {
    if (selectedImageId) {
      const image = reviewImages.find((image) => image.id === selectedImageId);
      if (image) {
        scrollToImage(selectedImageId);
      }
    }
  }, [selectedImageId, reviewImages, scrollToImage]);

  return (
    <div className="flex flex-col gap-4 items-start w-full">
      {hasReviewImages && (
        <>
          {isMobile ? (
            <div
              className="flex flex-row gap-3 w-full overflow-x-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {reviewImages.map((image) => (
                <img
                  key={image.id}
                  id={`review-image-${image.id}`}
                  src={image.url}
                  alt={image.alt}
                  className={`rounded-xl aspect-square object-cover shrink-0 cursor-pointer ${reviewImages.length === 1 ? 'w-full h-full' : 'w-[70vw] h-[70vw]'}`}
                  onClick={() => onImageClick(image)}
                />
              ))}
            </div>
          ) : (
            <div className="w-full h-[456px]">
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
                setApi={setCarouselApi}
              >
                <Carousel.Content>
                  {reviewImages.map((image) => (
                    <Carousel.Item key={image.id}>
                      <div className="h-[456px] flex items-center justify-center bg-generic-0">
                        <img
                          id={`review-image-${image.id}`}
                          src={image.url}
                          alt={image.alt}
                          className="rounded-xl object-contain h-[456px] cursor-pointer"
                          onClick={() => onImageClick(image)}
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel.Content>
                <Carousel.Previous className="w-[48px] h-[48px] bg-neutral-50 rounded-full left-0 border-none">
                  <LucideIcon name="ChevronLeft" width="36px" height="36px" className="text-neutral-400" />
                </Carousel.Previous>
                <Carousel.Next className="w-[48px] h-[48px] bg-neutral-50 rounded-full border-none right-0">
                  <LucideIcon name="ChevronRight" width="36px" height="36px" className="text-neutral-400" />
                </Carousel.Next>
              </Carousel>
            </div>
          )}
        </>
      )}
      <div className="flex flex-col gap-4 items-start w-full">
        {!isMobile && (
          <div className="flex flex-row justify-between items-center w-full">
            <div className="flex flex-row gap-1 items-center">
              <p className="text-heading-h5 font-semibold text-primary-400">{review.rating}</p>
              <StarIcon width={20} height={20} />
            </div>
            <p className="text-neutral-400 text-label-small font-normal">{formatRelativeTime(review.createdAt)}</p>
          </div>
        )}

        <div className="flex flex-col items-start gap-3 w-full">
          <p className="text-paragraph-large font-semibold text-neutral-800">{review.title}</p>
          <p className="text-paragraph-small font-normal text-neutral-500">{review.content}</p>
        </div>

        <div className="flex flex-row rounded-[8px] p-3 bg-neutral-50 w-full gap-3">
          <img src={avatar.src} alt="avatar" className="w-8 h-8 rounded-full shrink-0" />
          <div className="flex flex-col items-start gap-1">
            <p className="text-label-small font-medium text-neutral-800">{review.author.name}</p>
            <div className="flex flex-row gap-1 flex-wrap">
              {review.skinType.map((type, index) => (
                <p key={index} className="text-label-xsmall font-normal text-neutral-700">
                  {type}
                  {index < review.skinType.length - 1 && ' \u2022'}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ReviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ReviewImage[];
  reviews: Review[];
  initialSelectedReview?: Review | null;
  initialSelectedImageId?: string | null;
}

const ReviewDetailModal = ({
  isOpen,
  onClose,
  images,
  reviews,
  initialSelectedReview = null,
  initialSelectedImageId = null,
}: ReviewDetailModalProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [selectedReview, setSelectedReview] = useState<Review | null>(initialSelectedReview);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(initialSelectedImageId);
  const [imageDetailModalOpen, setImageDetailModalOpen] = useState(false);
  const [selectedImageForDetail, setSelectedImageForDetail] = useState<ReviewImage | null>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSelectedReview(initialSelectedReview);
      setSelectedImageId(initialSelectedImageId);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialSelectedReview, initialSelectedImageId]);

  const handleImageClick = (image: ReviewImage) => {
    const review = reviews.find((r) => r.id === image.reviewId);
    if (review) {
      setSelectedReview(review);
      setSelectedImageId(image.id);
    }
  };

  const handleImageDetailClick = (image: ReviewImage) => {
    setSelectedImageForDetail(image);
    setImageDetailModalOpen(true);
  };

  const reviewImages = selectedReview?.images || [];
  const selectedImageIndex = selectedImageForDetail
    ? reviewImages.findIndex((img) => img.id === selectedImageForDetail.id)
    : 0;

  const handleImageDetailClose = () => {
    setImageDetailModalOpen(false);
    setSelectedImageForDetail(null);
  };

  const modalTitle = selectedReview ? 'Review Detail' : 'Images from reviews';

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo) => {
    if (info.offset.y > 200 || info.velocity.y > 200) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {isMobile ? (
            <>
              <Overlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={handleOverlayClick}
              />
              <MobileDrawer
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                onDragEnd={handleDragEnd}
              >
                <div className="flex flex-row w-full justify-between items-center pt-6 px-4">
                  <p className="text-heading-h6-mobile font-normal">{modalTitle}</p>
                  <button
                    onClick={onClose}
                    aria-label="Close modal"
                    className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-neutral-100"
                  >
                    <LucideIcon name="X" width="24px" height="24px" className="text-generic-100" />
                  </button>
                </div>

                <div
                  className="flex-1 overflow-y-auto pt-4 px-4 pb-4"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {selectedReview ? (
                    <ReviewDetailContent
                      review={selectedReview}
                      selectedImageId={selectedImageId || undefined}
                      onImageClick={handleImageDetailClick}
                    />
                  ) : (
                    <ImageGalleryContent images={images} onImageClick={handleImageClick} />
                  )}
                </div>
              </MobileDrawer>
            </>
          ) : (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleOverlayClick}
            >
              <ModalContainer
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              >
                <div className="flex flex-row w-full justify-between items-center pt-6 px-6">
                  <p className="text-heading-h6-mobile font-semibold">{modalTitle}</p>
                  <button
                    onClick={onClose}
                    aria-label="Close modal"
                    className="inline-flex items-center justify-center rounded-md h-9 w-9 hover:bg-neutral-100"
                  >
                    <LucideIcon name="X" width="24px" height="24px" className="text-generic-100" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6">
                  {selectedReview ? (
                    <div className="pt-8 pb-6">
                      <ReviewDetailContent
                        review={selectedReview}
                        selectedImageId={selectedImageId || undefined}
                        onImageClick={handleImageDetailClick}
                      />
                    </div>
                  ) : (
                    <ImageGalleryContent images={images} onImageClick={handleImageClick} />
                  )}
                </div>
              </ModalContainer>
            </Overlay>
          )}
        </>
      )}

      <ReviewImageDetailModal
        isOpen={imageDetailModalOpen}
        onClose={handleImageDetailClose}
        image={selectedImageForDetail}
        images={reviewImages}
        initialIndex={selectedImageIndex >= 0 ? selectedImageIndex : 0}
      />
    </AnimatePresence>
  );
};

export default ReviewDetailModal;
